import React, { useMemo, useState } from "react";
import { COLUMNS, timing, fmtEuro } from "./crmUtils";

const PERIODS = [[7, "7 jours"], [30, "30 jours"], [90, "3 mois"], [0, "Depuis le début"]];

function Kpi({ label, value, sub, testid, dark }) {
  return (
    <div data-testid={testid} className={`rounded-[20px] p-4 ${dark ? "bg-[#2D181E] text-white" : "border border-line bg-white shadow-card"}`}>
      <p className={`text-[11px] font-medium uppercase tracking-[0.14em] ${dark ? "text-[#F3D2D9]" : "text-ink-muted"}`}>{label}</p>
      <p className="mt-1.5 font-sans text-2xl font-medium">{value}</p>
      {sub && <p className={`mt-0.5 text-xs ${dark ? "text-white/55" : "text-ink-muted"}`}>{sub}</p>}
    </div>
  );
}

export default function CrmDashboard({ state }) {
  const [days, setDays] = useState(30);
  const m = useMemo(() => {
    const now = Date.now();
    const from = days ? now - days * 864e5 : 0;
    const inP = (iso) => Date.parse(iso) >= from;
    const moves = state.cards.flatMap((c) => c.history.filter((h) => h.type === "move" && inP(h.at)).map((h) => h.column));
    const count = (col) => moves.filter((x) => x === col).length;
    const contacted = state.cards.filter((c) => inP(c.createdAt)).length;
    const relances = count("relance1") + count("relance2") + count("relance3") + state.cards.reduce((s, c) => s + c.history.filter((h) => h.type === "relance" && inP(h.at)).length, 0);
    const calls = count("appel"), won = count("gagne");
    const clients = state.cards.filter((c) => c.client);
    const ca = clients.filter((c) => c.client.signedAt && inP(c.client.signedAt)).reduce((s, c) => s + (Number(c.client.price) || 0), 0);
    const total = clients.reduce((s, c) => s + (Number(c.client.price) || 0), 0);
    const late = state.cards.filter((c) => timing(state, c, now)?.overdue).length;
    return { contacted, relances, calls, noshow: count("noshow"), won, lost: count("perdu"), ca, total, late,
      booking: contacted ? Math.round((calls / contacted) * 100) : 0, closing: calls ? Math.min(100, Math.round((won / calls) * 100)) : 0 };
  }, [state, days]);
  const max = Math.max(1, ...COLUMNS.map((c) => state.cards.filter((x) => x.column === c.id).length));
  return (
    <div data-testid="crm-dashboard" className="space-y-6">
      <div className="flex flex-wrap gap-1 rounded-full bg-white p-1 ring-1 ring-line sm:w-fit">
        {PERIODS.map(([d, l]) => <button key={d} data-testid={`crm-period-${d}`} onClick={() => setDays(d)} className={`rounded-full px-3.5 py-1.5 text-xs transition-colors ${days === d ? "bg-bordeaux font-medium text-white" : "text-ink-soft hover:text-bordeaux"}`}>{l}</button>)}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="crm-kpi-contacted" label="Prospects contactés" value={m.contacted} sub={`${m.relances} relances faites`} />
        <Kpi testid="crm-kpi-calls" label="Appels réservés" value={m.calls} sub={`${m.booking}% des prospects contactés`} />
        <Kpi testid="crm-kpi-won" label="Ventes gagnées" value={m.won} sub={`${m.noshow} no-show · ${m.lost} perdues`} />
        <Kpi testid="crm-kpi-closing" dark label="Taux de closing" value={`${m.closing}%`} sub="ventes gagnées / appels" />
        <Kpi testid="crm-kpi-ca" label="CA signé" value={fmtEuro(m.ca)} sub="sur la période" />
        <Kpi testid="crm-kpi-total-ca" label="CA total closé" value={fmtEuro(m.total)} sub="toutes périodes" />
        <Kpi testid="crm-kpi-late" label="À relancer maintenant" value={m.late} sub="cartes en retard" />
        <Kpi testid="crm-kpi-total" label="Cartes dans le pipeline" value={state.cards.length} />
      </div>
      <div className="rounded-[24px] border border-line bg-white p-5 shadow-card">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink-muted">Répartition actuelle du pipeline</p>
        <div className="mt-4 space-y-2.5">
          {COLUMNS.map((c) => { const n = state.cards.filter((x) => x.column === c.id).length; return (
            <div key={c.id} className="grid grid-cols-[130px_1fr_28px] items-center gap-3 text-xs sm:grid-cols-[190px_1fr_32px]">
              <span className="truncate text-ink-soft">{c.title}</span>
              <span className="h-2.5 overflow-hidden rounded-full bg-cream"><span className="block h-full rounded-full transition-all" style={{ width: `${(n / max) * 100}%`, background: c.color }} /></span>
              <span className="text-right font-medium text-ink">{n}</span>
            </div>
          ); })}
        </div>
      </div>
    </div>
  );
}
